import React, { useEffect, useState, useContext } from "react";
import axios from "axios";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Spinner,
  Modal,
  Badge,
  Alert,
} from "react-bootstrap";
import { BsChevronLeft, BsDownload } from "react-icons/bs";
import Header from "./Header";
import { getCachedData, setCachedData } from "../utils/apiCache";
import { ProductContext } from "../context/AppContext";
import {
  Person,
  Phone,
  CalendarEvent,
  Clock,
  Image as ImageIcon,
  FileText,
  Eye,
  XCircleFill,
  Plus,
} from "react-bootstrap-icons";
import { Link } from "react-router-dom";

const Cocurricular = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [printLoading, setPrintLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalImage, setModalImage] = useState(null);
  const { userId } = useContext(ProductContext);
  const authtoken = localStorage.getItem("token");
  const [studentId, setStudentId] = useState(null);
  const [sessionId, setSessionId] = useState(null);

  const dummyImage =
    "https://images.unsplash.com/photo-1535930749574-1399327ce78f?auto=format&fit=crop&w=600&q=80";

  const getLink = (urlField) => {
    if (!urlField) return null;
    if (typeof urlField === "string") {
      return urlField.trim() || null;
    }
    if (urlField && typeof urlField === "object" && urlField.link) {
      return urlField.link;
    }
    return null;
  };

  useEffect(() => {
    if (userId) fetchStudentId();
  }, [userId]);

  const fetchStudentId = async () => {
    try {
      const response = await axios.get(
        `https://api.mirai.healthcare/api/student?regs_id=${userId}`,
        {
          headers: { "x-auth": `${authtoken}` },
        }
      );
      if (response.data.success && response.data.message.length > 0) {
        const studentData = response.data.message[0];
        setStudentId(studentData.id);
        setSessionId(studentData.session); // Assuming session is available in student data
      } else {
        console.error("Student ID not found");
      }
    } catch (error) {
      console.error("Error fetching student ID:", error);
    }
  };

  useEffect(() => {
    if (studentId) fetchActivityData();
  }, [studentId]);

  const fetchActivityData = async () => {
    if (!studentId) return;
    const cacheKey = `cocurr_activities_${studentId}`;
    const cached = getCachedData(cacheKey);
    if (cached) {
      setActivities(cached);
      setLoading(false);
      
    }
    if (!cached) setLoading(true);
    try {
      const response = await axios.get(
        `https://api.mirai.healthcare/api/student-details/curriculam/student?student_id=${studentId}`,
        { headers: { "x-auth": `${authtoken}` } }
      );
      if (response.data.success) {
        setActivities(response.data.message || []);
        setCachedData(cacheKey, response.data.message || []);
      } else {
        console.error("No activities found");
      }
    } catch (error) {
      console.error("Error fetching activities:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAchievementDownloadAndPrint = async (activityId) => {
    try {
      setPrintLoading(true);

      const today = new Date();
      const issueDate = today.toISOString().split("T")[0];

      const payload = {
        userType: "student",
        documentType: "co-curricular_participation",
        studentId: studentId,
        session_id: sessionId,
        data: {
          curriculam_id: activityId,
          issueDate: issueDate,
        },
      };

      const response = await axios.post(
        `https://api.mirai.healthcare/api/head/diary/document/event-participation-certificate`,
        payload,
        {
          headers: {
            "x-auth": authtoken,
            "Content-Type": "application/json",
          },
          responseType: "text",
        }
      );

      const htmlContent = response.data;

      // Open in new window and print
      const printWindow = window.open("", "_blank", "width=800,height=600");
      printWindow.document.write(`
        <html>
          <head><title>Certificate</title></head>
          <body>${htmlContent}</body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      // printWindow.print();
      // printWindow.close();
    } catch (error) {
      console.error("Download/Print error:", error);
      alert("Failed to download and print.");
    } finally {
      setPrintLoading(false);
    }
  };

  const handleImageClick = (imageUrl) => {
    if (imageUrl && typeof imageUrl === "string") {
      setModalImage(imageUrl);
      setShowModal(true);
    }
  };

  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case "pending":
        return <Badge bg="warning">Pending</Badge>;
      case "rejected":
        return <Badge bg="danger">Rejected</Badge>;
      case "approved":
        return <Badge bg="success">Approved</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  const formatDate = (dateString) => {
    return dateString ? new Date(dateString).toLocaleDateString() : "N/A";
  };

  const renderDocumentPreview = (link, title) => {
    if (!link) {
      return (
        <div
          className="d-flex align-items-center justify-content-center bg-light rounded p-3"
          style={{ height: "150px" }}
        >
          <span className="text-muted">No Document</span>
        </div>
      );
    }
    return (
      <div
        className="rounded"
        style={{
          width: "100%",
          height: "150px",
          overflow: "hidden",
          cursor: "pointer",
        }}
        onClick={() => handleImageClick(link)}
      >
        <img
          src={link}
          alt={title}
          className="w-100 h-100 object-fit-cover"
          onError={(e) => {
            e.target.src = dummyImage;
          }}
        />
      </div>
    );
  };

  const renderSmallDocument = (link, title) => {
    if (!link) {
      return (
        <Col xs={6} md={4} className="mb-2">
          <p className="text-muted small mb-1">{title}</p>
          <div
            className="d-flex align-items-center justify-content-center bg-light rounded p-2"
            style={{ height: "80px" }}
          >
            <span className="text-muted small">No Document</span>
          </div>
        </Col>
      );
    }
    return (
      <Col xs={6} md={4} className="mb-2">
        <p className="text-muted small mb-1">{title}</p>
        <img
          src={link}
          alt={title}
          className="img-thumbnail w-100"
          style={{ height: "80px", objectFit: "cover", cursor: "pointer" }}
          onClick={() => handleImageClick(link)}
          onError={(e) => {
            e.target.src = dummyImage;
          }}
        />
      </Col>
    );
  };

  if (loading) {
    return (
      <div className="page-wrapper">
        <Header />
        <Container className="mt-5 text-center">
          <Spinner animation="border" variant="primary" />
        </Container>
      </div>
    );
  }

  return (
    // <div className="page-wrapper">
    //   <Header />

    //   <Container fluid className="mt-4">

    //     <div className="d-flex justify-content-between align-items-center mb-4">
    //       <h2 className="fw-bold">Co-Curricular Activities</h2>
    //       <Button variant="outline-secondary" onClick={() => window.history.back()}>
    //         <BsChevronLeft className="me-1" /> Back
    //       </Button>
    //     </div>

    <div className="page-wrapper min-vh-100">
      <Header />

      <div className="content px-4 pt-4">
        {/* Header */}

        <div className="d-flex justify-content-between align-items-center my-2 pb-3">
          <div>
            <h2
              className="page-title fw-bold mb-0"
              style={{ letterSpacing: "0.5px" }}
            >
              Co-Curricular Activities
            </h2>
          </div>
        </div>
        <Container fluid>
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white border-0 pb-0">
              <h4 className="mb-0">Co-Curricular Timeline</h4>
            </div>

            <div className="card-body py-4">
              <div className="timeline position-relative">
                {activities.map((act) => (
                  <div key={act.id} className="timeline-item mb-5">
                    <div className="timeline-dot bg-primary rounded-circle">
                      <CalendarEvent className="text-white" />
                    </div>

                    <Card className="timeline-content shadow-sm p-4 border-0 hover-lift">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div>
                          <h5 className="mb-1 text-primary">
                            {act.ctgr?.name || "Activity"}
                          </h5>
                          {act.sub_ctgr?.title && (
                            <h6 className="mb-1 text-muted">
                              {act.sub_ctgr.title}
                            </h6>
                          )}

                          <small className="text-muted">
                            <strong>Guardian: </strong>
                            {act.guardian || "N/A"}
                          </small>
                        </div>

                        <Badge className={getStatusBadge(act.status)}>
                          {act.status}
                        </Badge>
                      </div>

                      <div className="row NewBorderTop pt-2">
                        <div className="col-md-6">
                          <p className="text-muted mb-2 fs-14 text-black">
                            <strong>Relation: </strong>
                            {act.rel_child || "N/A"}
                          </p>
                        </div>
                        <div className="col-md-6 text-md-end">
                          <p className="text-muted mb-2 fs-14 text-black">
                            <strong>Date: </strong>
                            {formatDate(act.sub_ctgr?.due_date)}
                          </p>
                        </div>
                      </div>

                      {(act.reward || act.grade) && (
                        <div className="row pt-2">
                          {act.reward && (
                            <div className="col-md-6">
                              <p className="text-muted mb-2 fs-14 text-black">
                                <strong>Reward: </strong> {act.reward}
                              </p>
                            </div>
                          )}
                          {act.grade && (
                            <div className="col-md-6 text-md-end">
                              <p className="text-muted mb-2 fs-14 text-black">
                                <strong>Grade: </strong> {act.grade}
                              </p>
                            </div>
                          )}
                        </div>
                      )}

                      <div className="gap-2 mt-2 text-md-end">
                        {act.doc_url && (
                          <Button
                            variant="outline-info"
                            size="sm"
                            className="me-2"
                            onClick={() => {
                              setModalImage(getLink(act.doc_url));
                              setShowModal(true);
                            }}
                          >
                            <ImageIcon size={14} /> Activity Photo
                          </Button>
                        )}

                        {act.parent_url && (
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            className="me-2"
                            onClick={() => {
                              setModalImage(getLink(act.parent_url));
                              setShowModal(true);
                            }}
                          >
                            <FileText size={14} /> Parent Doc
                          </Button>
                        )}

                        {act.performance_url && (
                          <Button
                            variant="outline-success"
                            size="sm"
                            className="me-2"
                            onClick={() => {
                              const link = getLink(act.performance_url);
                              if (link) window.open(link, "_blank");
                            }}
                          >
                            <FileText size={14} /> Performance
                          </Button>
                        )}
                      </div>

                      <div className="mt-3 p-3 bg-light rounded">
                        <div className="row pt-2">
                          {act.rmk && (
                            <div className="col-md-6">
                              <p className="text-muted mb-2 fs-14 text-black">
                                <strong>Remark:</strong> {act.rmk}
                              </p>
                            </div>
                          )}
                          {act.admin_rmk && (
                            <div className="col-md-6 text-md-end">
                              <p className="text-muted mb-2 fs-14 text-black">
                                <strong>Admin Remark:</strong> {act.admin_rmk}
                              </p>
                            </div>
                          )}
                        </div>

                        {act.status?.toLowerCase() === "approved" && (
                          <div className="text-end">
                            <Button
                              size="sm"
                              variant="primary"
                              onClick={() =>
                                handleAchievementDownloadAndPrint(act.id)
                              }
                            >
                              <Eye className="me-2" />{" "}
                              {printLoading
                                ? "Generating..."
                                : "View Certificate"}
                            </Button>
                          </div>
                        )}
                      </div>
                    </Card>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Container>
      </div>

      {/* IMAGE MODAL */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="lg"
        centered
      >
        <Modal.Body className="p-1">
          <img
            src={modalImage}
            className="w-100"
            alt=""
            style={{
              width: "100%",
              height: "500px",
              objectFit: "cover",
              objectPosition: "top",
            }}
          />
        </Modal.Body>
      </Modal>

      {/* Styles from Visitor.js */}
      <style jsx>{`
        .timeline {
          padding-left: 40px;
        }
        .timeline::before {
          content: "";
          position: absolute;
          left: 20px;
          top: 0;
          bottom: 0;
          width: 3px;
          background: linear-gradient(to bottom, #0d6efd, #0dcaf0);
        }
        .timeline-dot {
          position: absolute;
          left: -40px;
          top: 15px;
          width: 40px;
          height: 40px;
          background: #0d6efd;
          border: 5px solid #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 0 0 4px #cfe2ff;
        }
        .hover-lift:hover {
          transform: translateY(-6px);
          box-shadow: 0 12px 30px rgba(13, 110, 253, 0.2);
        }
      `}</style>
    </div>
  );
};

export default Cocurricular;
